import { message, Tooltip } from 'antd';
import { capitalize } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { CategoryEnum } from '../../../__generated__/graphql';
import { LOCAL_STORAGE } from '../../../common/constants';
import { getItem } from '../../../common/utils';
import { Detection } from '../../../types/reportAnnotationScreen.type';
import '../styles/annotationViewScreen.less';

export default function AnnotationScreen({
  data,
  onBoxClick,
  selectedDetectionId,
}: {
  data: Detection[];
  onBoxClick: (id: string) => void;
  selectedDetectionId: string | number | null;
}) {
  const imageRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [renderSize, setRenderSize] = useState({ width: 0, height: 0 });
  const imageDataRaw = getItem(LOCAL_STORAGE.FLOOR_PLAN);
  let baseCategoryKey: string | undefined = undefined;

  if (imageDataRaw) {
    try {
      const imageData = JSON.parse(imageDataRaw);
      const baseAsset = imageData?.assets?.find(
        (asset: { category: string; key: string }) =>
          asset.category === CategoryEnum.Base,
      );
      baseCategoryKey = baseAsset?.key || '';
    } catch (err) {
      message.error('Invalid data in floor plan');
    }
  }

  const updateSizes = () => {
    const img = imageRef.current;
    if (img) {
      setNaturalSize({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      const rect = img.getBoundingClientRect();
      setRenderSize({
        width: rect.width,
        height: rect.height,
      });
    }
  };

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    if (img.complete) {
      updateSizes();
    } else {
      img.onload = updateSizes;
    }

    window.addEventListener('resize', updateSizes);
    return () => {
      window.removeEventListener('resize', updateSizes);
    };
  }, []);

  const scaleBbox = (
    bbox: [number, number, number, number],
  ): { x: number; y: number; width: number; height: number } => {
    const { width: natW, height: natH } = naturalSize;
    const { width: renderW, height: renderH } = renderSize;

    if (natW === 0 || natH === 0) return { x: 0, y: 0, width: 0, height: 0 };

    const scaleX = renderW / natW;
    const scaleY = renderH / natH;

    const x = bbox[0] * scaleX;
    const y = bbox[1] * scaleY;
    const width = (bbox[2] - bbox[0]) * scaleX;
    const height = (bbox[3] - bbox[1]) * scaleY;

    return { x, y, width, height };
  };

  return (
    <div className="screen-container">
      <div className="image-canvas-wrapper" style={{ position: 'relative' }}>
        <img
          ref={imageRef}
          src={baseCategoryKey}
          alt="Floor Plan"
          className="annotation-display-image"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
        {data.map((detection, index) => {
          if (detection.bbox.length === 4) {
            const { x, y, width, height } = scaleBbox(detection.bbox);

            return (
              <Tooltip
                key={index}
                title={capitalize(detection.title || '')}
                placement="top"
              >
                <Rnd
                  enableResizing={false}
                  disableDragging={true}
                  bounds="parent"
                  // have given inline style as the css is conditioanl and will be frequently changing
                  style={{
                    zIndex: 10,
                    cursor: 'pointer',
                    position: 'absolute',
                    pointerEvents: 'auto',
                    border:
                      selectedDetectionId === (detection?.id || index)
                        ? '3px solid blue'
                        : '2px solid red',
                    backgroundColor:
                      selectedDetectionId === (detection?.id || index)
                        ? 'rgba(0, 0, 255, 0.2)'
                        : 'rgba(255, 0, 0, 0.2)',
                  }}
                  size={{ width, height }}
                  position={{ x, y }}
                  onClick={() => onBoxClick(detection.id)}
                >
                  {detection?.issue && (
                    <div className="circle-wrapper">
                      <div className="circles">
                        <div className="circle1"></div>
                        <div className="circle2"></div>
                        <div className="circle3"></div>
                      </div>
                    </div>
                  )}
                </Rnd>
              </Tooltip>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
